import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Trash2, Phone, Mail, CalendarDays, Users, BedDouble } from "lucide-react";
import Logo from "../components/Logo";
import AdminReviews from "../components/AdminReviews";
import { ROOMS, formatApiErrorDetail } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "confirmed", "closed"];
const STATUS_COLORS = {
  new: "text-[#C5A059] border-[#C5A059]/50",
  contacted: "text-sky-300 border-sky-300/40",
  confirmed: "text-emerald-300 border-emerald-300/40",
  closed: "text-[#78716C] border-[#2A2421]",
};

const roomName = (id) => ROOMS.find((r) => r.id === id)?.name || id;

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("rayan_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoadingData(true);
    try {
      const [e, m, r] = await Promise.all([
        axios.get(`${API}/enquiries`, authHeaders()),
        axios.get(`${API}/contact`, authHeaders()),
        axios.get(`${API}/reviews/all`, authHeaders()),
      ]);
      setEnquiries(e.data);
      setMessages(m.data);
      setReviews(r.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("rayan_admin_token");
        setToken("");
      } else {
        toast.error("Could not load dashboard data");
      }
    } finally {
      setLoadingData(false);
    }
  }, [token, authHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("rayan_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, authHeaders());
    } catch {}
    localStorage.removeItem("rayan_admin_token");
    setToken("");
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, authHeaders());
      setEnquiries((prev) => prev.map((en) => (en.id === id ? { ...en, status } : en)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const removeEnquiry = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, authHeaders());
      setEnquiries((prev) => prev.filter((en) => en.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete enquiry");
    }
  };

  const removeMessage = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`, authHeaders());
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Message deleted");
    } catch {
      toast.error("Could not delete message");
    }
  };

  const setReviewStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/reviews/${id}`, { status }, authHeaders());
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(status === "approved" ? "Review published on the website" : `Review marked ${status}`);
    } catch {
      toast.error("Could not update review");
    }
  };

  const removeReview = async (id) => {
    try {
      await axios.delete(`${API}/reviews/${id}`, authHeaders());
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted");
    } catch {
      toast.error("Could not delete review");
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0D0B0A] px-5" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm border border-[#2A2421] bg-[#161311] p-9">
          <div className="mb-8 flex justify-center">
            <Logo />
          </div>
          <p className="mb-6 text-center text-[10px] uppercase tracking-[0.3em] text-[#8C713B]">Hotel Management</p>
          <label className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-[#8C713B]" htmlFor="ad-email">Email</label>
          <input
            id="ad-email"
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mb-5 w-full border-b border-[#2A2421] bg-transparent py-2 text-sm text-[#F5F2EB] outline-none focus:border-[#C5A059]"
            placeholder="admin@therayan.in"
          />
          <label className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-[#8C713B]" htmlFor="ad-pass">Password</label>
          <input
            id="ad-pass"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mb-6 w-full border-b border-[#2A2421] bg-transparent py-2 text-sm text-[#F5F2EB] outline-none focus:border-[#C5A059]"
            placeholder="••••••••"
          />
          {error && (
            <p data-testid="admin-login-error" className="mb-4 text-center text-xs text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 border border-[#C5A059] bg-[#C5A059] px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#0D0B0A] transition-all hover:bg-transparent hover:text-[#C5A059] disabled:opacity-60"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {busy ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  const newCount = enquiries.filter((e) => e.status === "new").length;
  const pendingReviews = reviews.filter((r) => r.status === "pending").length;

  return (
    <div className="min-h-screen bg-[#0D0B0A] text-[#F5F2EB]" data-testid="admin-dashboard">
      <header className="border-b border-[#2A2421] bg-[#161311]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Logo />
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={fetchData}
              className="flex items-center gap-2 border border-[#2A2421] px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#A8A29E] transition-colors hover:border-[#C5A059] hover:text-[#C5A059]"
            >
              <RefreshCw size={13} className={loadingData ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 border border-[#2A2421] px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#A8A29E] transition-colors hover:border-red-400 hover:text-red-400"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="border border-[#2A2421] bg-[#161311] p-5" data-testid="stat-total-enquiries">
            <p className="font-display text-3xl text-[#C5A059]">{enquiries.length}</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#78716C]">Total Enquiries</p>
          </div>
          <div className="border border-[#2A2421] bg-[#161311] p-5" data-testid="stat-new-enquiries">
            <p className="font-display text-3xl text-[#C5A059]">{newCount}</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#78716C]">Awaiting Reply</p>
          </div>
          <div className="border border-[#2A2421] bg-[#161311] p-5" data-testid="stat-messages">
            <p className="font-display text-3xl text-[#C5A059]">{messages.length}</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#78716C]">Contact Messages</p>
          </div>
          <div className="border border-[#2A2421] bg-[#161311] p-5" data-testid="stat-pending-reviews">
            <p className="font-display text-3xl text-[#C5A059]">{pendingReviews}</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#78716C]">Reviews to Approve</p>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          {["enquiries", "messages", "reviews"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`border px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] transition-all ${
                tab === t ? "border-[#C5A059] bg-[#C5A059] text-[#0D0B0A]" : "border-[#2A2421] text-[#A8A29E] hover:border-[#C5A059] hover:text-[#C5A059]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="space-y-4" data-testid="admin-enquiries-list">
            {enquiries.length === 0 && (
              <p className="border border-[#2A2421] bg-[#161311] p-10 text-center text-sm text-[#78716C]" data-testid="admin-enquiries-empty">
                No booking enquiries yet.
              </p>
            )}
            {enquiries.map((en) => (
              <article key={en.id} data-testid={`admin-enquiry-${en.id}`} className="border border-[#2A2421] bg-[#161311] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl text-[#F5F2EB]">{en.name}</h3>
                    <p className="mt-1 text-xs text-[#C5A059]">{roomName(en.room_type)}</p>
                  </div>
                  <span className={`border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${STATUS_COLORS[en.status] || STATUS_COLORS.new}`} data-testid={`admin-enquiry-status-${en.id}`}>
                    {en.status}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#A8A29E]">
                  <span className="flex items-center gap-1.5"><CalendarDays size={13} className="text-[#8C713B]" /> {en.check_in} → {en.check_out}</span>
                  <span className="flex items-center gap-1.5"><Users size={13} className="text-[#8C713B]" /> {en.guests} guests</span>
                  <a href={`tel:${en.phone}`} className="flex items-center gap-1.5 hover:text-[#C5A059]"><Phone size={13} className="text-[#8C713B]" /> {en.phone}</a>
                  {en.email && <a href={`mailto:${en.email}`} className="flex items-center gap-1.5 hover:text-[#C5A059]"><Mail size={13} className="text-[#8C713B]" /> {en.email}</a>}
                  <span className="flex items-center gap-1.5"><BedDouble size={13} className="text-[#8C713B]" /> {new Date(en.created_at).toLocaleString("en-IN")}</span>
                </div>
                {en.notes && <p className="mt-3 border-l-2 border-[#C5A059]/40 pl-3 text-sm italic text-[#A8A29E]">“{en.notes}”</p>}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <select
                    data-testid={`admin-status-select-${en.id}`}
                    value={en.status}
                    onChange={(e) => setStatus(en.id, e.target.value)}
                    className="cursor-pointer border border-[#2A2421] bg-[#0D0B0A] px-3 py-2 text-xs text-[#F5F2EB] outline-none focus:border-[#C5A059]"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <button
                    data-testid={`admin-delete-enquiry-${en.id}`}
                    onClick={() => removeEnquiry(en.id)}
                    className="flex items-center gap-1.5 border border-[#2A2421] px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#78716C] transition-colors hover:border-red-400 hover:text-red-400"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-4" data-testid="admin-messages-list">
            {messages.length === 0 && (
              <p className="border border-[#2A2421] bg-[#161311] p-10 text-center text-sm text-[#78716C]" data-testid="admin-messages-empty">
                No contact messages yet.
              </p>
            )}
            {messages.map((m) => (
              <article key={m.id} data-testid={`admin-message-${m.id}`} className="border border-[#2A2421] bg-[#161311] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl text-[#F5F2EB]">{m.name}</h3>
                    <p className="mt-1 text-xs text-[#78716C]">{new Date(m.created_at).toLocaleString("en-IN")}</p>
                  </div>
                  <button
                    data-testid={`admin-delete-message-${m.id}`}
                    onClick={() => removeMessage(m.id)}
                    className="flex items-center gap-1.5 border border-[#2A2421] px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#78716C] transition-colors hover:border-red-400 hover:text-red-400"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#A8A29E]">
                  {m.phone && <a href={`tel:${m.phone}`} className="flex items-center gap-1.5 hover:text-[#C5A059]"><Phone size={13} className="text-[#8C713B]" /> {m.phone}</a>}
                  {m.email && <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 hover:text-[#C5A059]"><Mail size={13} className="text-[#8C713B]" /> {m.email}</a>}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#D6CDBE]">{m.message}</p>
              </article>
            ))}
          </div>
        )}

        {tab === "reviews" && <AdminReviews reviews={reviews} onStatus={setReviewStatus} onDelete={removeReview} />}
      </main>
    </div>
  );
};

export default Admin;
